import React, { FC, useState, useRef, useCallback, useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import { Search } from 'components/Search/Search';
import { Row } from 'components/UI/Row';
import { AlertDialog } from 'components/UI/Dialog/AlertDialog';
import { PageIndex } from 'components/UI/PageIndex';

import { axiosInstance } from '../axiosInstance';

interface AclFeaturesListProps {
  apiPrefix: string;
  feature: string;
  resourceName: string;
}

type Pagination = {
  index: number;
  indexesCount?: number;
  searchTerm?: string;
  size?: number;
};

const AclFeaturesList: FC<AclFeaturesListProps> = ({ feature, apiPrefix, resourceName }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const [clicked, setClicked] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [isDeletingItems, setIsDeletingItems] = useState<{ [id: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({
    index: 1
  });
  // TODO: make custom hook for table with ref
  const tableRef = useRef<HTMLTableElement>(null);

  const [resourcesList, setResourcesList] = useState([]);
  const fetchResources = useCallback(async (page: number, searchTerm?: string) => {
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get(apiPrefix, {
        params: { index: page, size: 30, search: searchTerm }
      });
      const { index, indexes_count: indexesCount, size } = data;
      setResourcesList(data[resourceName]);
      setPagination({ index, indexesCount, size, searchTerm });
      if (tableRef) {
        tableRef?.current?.scrollIntoView();
        window.scrollTo({ top: 0 });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources(1);
  }, []);

  const deleteResource = async (id: string) => {
    try {
      setIsDeletingItems({ ...isDeletingItems, [id]: true });
      await axiosInstance.delete(`${apiPrefix}/${id}`);
      // fetchResources(1)
      setResourcesList(resourcesList.filter((r: any) => r.id !== id));
    } catch (e) {
      // display errors
    } finally {
      setIsDeletingItems({ ...isDeletingItems, [id]: true });
    }
  };

  return (
    <>
      <div>
        <Row justifyContent="space-between" alignItems="center" margin="0 0 30px 0">
          <Search
            resetFunction={() => fetchResources(1)}
            searchFunction={(st: string) => fetchResources(1, st)}
            searchLabel={feature}
          />
          <div style={{ display: 'flex', flex: 0.5 }} />
          <Button
            variant="contained"
            disabled={/* isLoading */ false}
            color="primary"
            onClick={() => history.push(`${path}/new`)}
          >
            {t(`acl.${feature}.add new`)}
          </Button>
        </Row>
      </div>
      <TableContainer component={Paper} elevation={3} ref={tableRef}>
        <Table stickyHeader aria-label="policies">
          <TableHead>
            <TableRow>
              <TableCell>{t(`acl.${feature}.list label`)}</TableCell>
              <TableCell style={{ width: '5%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell>
                  <Row height="50vh" justifyContent="center" alignItems="center" />
                </TableCell>
              </TableRow>
            )}
            {resourcesList.map((el: any) => (
              <React.Fragment key={el.id}>
                <TableRow
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    history.push(`${path}/${el.id}`);
                  }}
                >
                  <TableCell>{el.name}</TableCell>
                  <TableCell style={{ width: '5%' }}>
                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      disabled={isDeletingItems[el.id]}
                      onClick={e => {
                        e.stopPropagation();
                        setClicked(el.id);
                        setShowConfirmDelete(true);
                      }}
                    >
                      <DeleteIcon />
                      {isDeletingItems && isDeletingItems[el.id] && (
                        <CircularProgress
                          color="secondary"
                          size={34}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: -17,
                            marginLeft: -17
                          }}
                        />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {isEmpty(resourcesList) && (
              <TableRow>
                <TableCell align="center">{t(`acl.${feature}.no data`)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={(_, page: number) => fetchResources(page, pagination.searchTerm)}
      />
      <AlertDialog
        handleAccept={() => {
          if (clicked) {
            deleteResource(clicked);
          }
          setClicked('');
          setShowConfirmDelete(false);
        }}
        handleCancel={() => {
          setClicked('');
          setShowConfirmDelete(false);
        }}
        open={showConfirmDelete}
        title={t(`acl.${feature}.delete title`)}
        text={t(`acl.${feature}.delete confirm text`)}
      />
    </>
  );
};

export { AclFeaturesList };
